/**
 * Shared formatting utilities for phone numbers, dates, and times
 * Used by both frontend and backend
 */
export const PHONE_FORMAT_OPTIONS = [
    { value: '(XXX) XXX-XXXX', label: '(555) 123-4567 - US Standard' },
    { value: 'XXX-XXX-XXXX', label: '555-123-4567 - Dashed' },
    { value: 'XXX.XXX.XXXX', label: '555.123.4567 - Dotted' },
    { value: '+1 (XXX) XXX-XXXX', label: '+1 (555) 123-4567 - US International' },
    { value: '+X XXXXXXXXXX', label: '+1 5551234567 - International Raw' },
];
/**
 * Format a phone number according to the specified format
 * @param phone - Raw phone number string
 * @param format - Phone format pattern (default: US Standard)
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(phone, format = '(XXX) XXX-XXXX') {
    if (!phone)
        return '';
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    // Extract digits only for formatting
    const digits = cleaned.replace(/\D/g, '');
    // Handle different digit lengths
    let countryCode = '';
    let areaCode = '';
    let exchange = '';
    let subscriber = '';
    if (digits.length === 10) {
        // US number without country code
        areaCode = digits.slice(0, 3);
        exchange = digits.slice(3, 6);
        subscriber = digits.slice(6);
        countryCode = '1';
    }
    else if (digits.length === 11 && digits.startsWith('1')) {
        // US number with country code
        countryCode = '1';
        areaCode = digits.slice(1, 4);
        exchange = digits.slice(4, 7);
        subscriber = digits.slice(7);
    }
    else if (digits.length > 10) {
        // International number
        const countryCodeLength = digits.length - 10;
        countryCode = digits.slice(0, countryCodeLength);
        areaCode = digits.slice(countryCodeLength, countryCodeLength + 3);
        exchange = digits.slice(countryCodeLength + 3, countryCodeLength + 6);
        subscriber = digits.slice(countryCodeLength + 6);
    }
    else {
        // Return as-is if we can't parse it
        return phone;
    }
    // Apply the format
    switch (format) {
        case '(XXX) XXX-XXXX':
            return `(${areaCode}) ${exchange}-${subscriber}`;
        case 'XXX-XXX-XXXX':
            return `${areaCode}-${exchange}-${subscriber}`;
        case 'XXX.XXX.XXXX':
            return `${areaCode}.${exchange}.${subscriber}`;
        case '+1 (XXX) XXX-XXXX':
            return `+${countryCode} (${areaCode}) ${exchange}-${subscriber}`;
        case '+X XXXXXXXXXX':
            return `+${countryCode} ${areaCode}${exchange}${subscriber}`;
        default:
            // Default to US Standard
            return `(${areaCode}) ${exchange}-${subscriber}`;
    }
}
/**
 * Get a sample formatted phone number for preview
 * @param format - Phone format pattern
 * @returns Sample formatted phone number
 */
export function getSamplePhoneNumber(format) {
    return formatPhoneNumber('5551234567', format);
}
// Payment Terms formatting
export const PAYMENT_TERMS_LABELS = {
    'due_on_receipt': 'Due on Receipt',
    'net15': 'Net 15',
    'net30': 'Net 30',
    'net60': 'Net 60',
    'due_on_completion': 'Due on Completion',
    '50_percent_upfront': '50% Upfront',
    'custom': 'Custom'
};
/** Payment term keys that show the credit limit field when selected (0 / null = no limit). */
export const PAYMENT_TERMS_REQUIRING_CREDIT_LIMIT = ['net15', 'net30', 'net60', 'custom'];
export function paymentTermsRequiresCreditLimit(terms) {
    if (!terms)
        return false;
    return PAYMENT_TERMS_REQUIRING_CREDIT_LIMIT.includes(terms);
}
/**
 * Days to add to the invoice date for a due date.
 * Returns null when due date should not be auto-calculated (custom / percent upfront).
 */
export function paymentTermsToDueDays(terms) {
    switch (terms) {
        case 'net15':
            return 15;
        case 'net30':
            return 30;
        case 'net60':
            return 60;
        case 'due_on_receipt':
        case 'due_on_completion':
            // Short grace window so same-day delivery/receipt still has a concrete due date
            return 3;
        case '50_percent_upfront':
        case 'custom':
            return null;
        default:
            return 30;
    }
}
/**
 * Format payment terms to human-readable text
 * @param terms - Raw payment terms string from database
 * @returns Human-readable payment terms
 */
export function formatPaymentTerms(terms) {
    if (!terms)
        return 'Net 30';
    return PAYMENT_TERMS_LABELS[terms] || terms;
}
/** Small words kept lowercase in title case (unless first/last). */
const NAV_TITLE_CASE_SMALL = new Set([
    'a',
    'an',
    'the',
    'and',
    'or',
    'but',
    'for',
    'nor',
    'on',
    'at',
    'to',
    'by',
    'of',
    'in',
    'as',
    'vs',
    'via',
    'with'
]);
/** Known short acronyms to keep uppercase in nav labels. */
const NAV_ACRONYMS = new Set(['hvac', 'diy', 'seo', 'faq', 'usa', 'uk', 'ai', 'crm', 'llc', 'inc']);
/**
 * Title-case a navigation label using common website/nav conventions.
 * Keeps known acronyms (HVAC, DIY) and lowercases small words mid-phrase.
 */
export function toNavTitleCase(input) {
    const words = input.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
    return words
        .map((word, index) => {
        const isEdge = index === 0 || index === words.length - 1;
        return word
            .split('-')
            .map((part) => {
            if (!part)
                return part;
            const lower = part.toLowerCase();
            if (!isEdge && NAV_TITLE_CASE_SMALL.has(lower))
                return lower;
            if (NAV_ACRONYMS.has(lower))
                return lower.toUpperCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
            .join('-');
    })
        .join(' ');
}
/**
 * True for blog/meta chrome that should never become a nav item
 * (comment counts, "read more", share links, bare dates, etc.).
 */
export function isJunkNavLabel(label) {
    const l = (label || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!l)
        return true;
    if (/^\d+\s+comments?$/.test(l))
        return true;
    if (/^(no|zero|leave a?|add a?|post a?)\s+comments?$/.test(l))
        return true;
    if (/^comments?$/.test(l))
        return true;
    if (/^(read more|continue reading|view (location|post|more|details?|map)|learn more|see more|show more)\b/.test(l)) {
        return true;
    }
    if (/^(share|tweet|pin it|like this|reply|edit|posted (on|in)|by\s+\w)/.test(l))
        return true;
    if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(l))
        return true;
    if (/^(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}/i.test(l)) {
        return true;
    }
    return false;
}
/**
 * Normalize a nav label for header/footer: reject placeholders, title-case, shorten.
 * Returns '' when the label is junk meta (callers should skip those items).
 */
export function normalizeNavLabel(label, options) {
    const maxLength = options?.maxLength ?? 28;
    const cleaned = (label || '').trim().replace(/\s+/g, ' ');
    if (isJunkNavLabel(cleaned))
        return '';
    const isPlaceholder = !cleaned ||
        /^link(\s*\d*)?$/i.test(cleaned) ||
        cleaned === '#' ||
        /^click here$/i.test(cleaned) ||
        /^menu item$/i.test(cleaned);
    let source = cleaned;
    if (isPlaceholder) {
        const fromSlug = (options?.fallbackSlug || '')
            .replace(/^\//, '')
            .split(/[-_/]/)
            .filter(Boolean)
            .join(' ');
        source = fromSlug || 'Page';
    }
    if (isJunkNavLabel(source))
        return '';
    let result = toNavTitleCase(source);
    if (result.length > maxLength) {
        const cut = result.slice(0, maxLength);
        const lastSpace = cut.lastIndexOf(' ');
        result = (lastSpace > 10 ? cut.slice(0, lastSpace) : cut).trim();
    }
    return result;
}
/** True when a nav item should render as a primary CTA button. */
export function isNavCtaLabel(label, url) {
    const haystack = `${label} ${url || ''}`.toLowerCase();
    return /quote|contact|book|schedule|get started|call us|request/.test(haystack);
}
export default {
    formatPhoneNumber,
    getSamplePhoneNumber,
    formatPaymentTerms,
    toNavTitleCase,
    normalizeNavLabel,
    isJunkNavLabel,
    isNavCtaLabel,
    PHONE_FORMAT_OPTIONS,
    PAYMENT_TERMS_LABELS,
};
//# sourceMappingURL=formatters.js.map